import { myWorkspacesOptions } from '@doneverse/data/queries'
import type { MeWorkspace } from '@doneverse/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { resetWorkspaceScopedQueries } from '@/lib/workspace-bootstrap'
import { useDashboardStore } from '@/store'

interface UseWorkspaceResult {
  workspaces: MeWorkspace[]
  currentWorkspace: MeWorkspace | null
  workspaceId: string | null
  isLoading: boolean
  setWorkspaceId: (id: string) => void
}

export function useWorkspace(): UseWorkspaceResult {
  const { isAuthenticated, profile } = useAuth()
  const queryClient = useQueryClient()
  const userId = profile?.id ?? null
  const workspaceId = useDashboardStore((s) => (userId ? (s.workspaceIdsByUserId[userId] ?? null) : null))

  const { data: workspaces = [], isLoading } = useQuery({
    ...myWorkspacesOptions(userId),
    enabled: isAuthenticated && !!profile && !!userId,
  })

  const setWorkspaceId = useCallback(
    (id: string) => {
      if (!userId) return
      const store = useDashboardStore.getState()
      if (id === (store.workspaceIdsByUserId[userId] ?? null)) return
      store.setWorkspaceId(userId, id)
      // Workspace-scoped queries must refetch with the new header — and must
      // not keep serving the previous founder's data while they do.
      void resetWorkspaceScopedQueries(queryClient)
    },
    [queryClient, userId],
  )

  const currentWorkspace = useMemo(
    () => workspaces.find((w) => w.id === workspaceId) ?? null,
    [workspaces, workspaceId],
  )

  return { workspaces, currentWorkspace, workspaceId, isLoading, setWorkspaceId }
}

/**
 * Mount once near the app root. Mid-session safety net: boot-time selection is
 * handled by `bootstrapWorkspaceSelection` inside the profile queryFn, but the
 * workspace list can also refetch on its own (staleTime expiry, focus). If a
 * refetch reveals the stored id is no longer a membership, fall back to the
 * first membership so requests don't 403. Routes through `setWorkspaceId` so
 * any queries that already fired with the stale header get invalidated.
 */
export function WorkspaceBootstrap(): null {
  const { workspaces, workspaceId, setWorkspaceId } = useWorkspace()

  useEffect(() => {
    if (workspaces.length === 0) return
    if (workspaceId && workspaces.some((w) => w.id === workspaceId)) return
    setWorkspaceId(workspaces[0].id)
  }, [workspaces, workspaceId, setWorkspaceId])

  return null
}
